import {
  type ButtonProps,
  Chip,
  Dropdown,
  DropdownItem,
  type DropdownItemProps,
  DropdownMenu,
  type DropdownProps,
  DropdownTrigger,
  type SharedSelection,
} from '@heroui/react';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { BiChevronDown } from 'react-icons/bi';
import { TiDeleteOutline } from 'react-icons/ti';
import { Button, cn } from '../../index';

type DropdownItemData = {
  key: string;
  description?: string;
  shortcut?: string;
  startContent?: React.ReactNode;
  title: string;
  color?: DropdownItemProps['color'];
  className?: string;
};

type Props = Omit<DropdownProps, 'type' | 'items' | 'children'> & {
  children: React.ReactNode;
  options: DropdownItemProps<DropdownItemData>[];
  selectedKeys?: string[];
  onSelectionChange?: (keys: SharedSelection) => void;
  selectionMode?: 'single' | 'multiple';
  triggerClassName?: string;
  triggerProps?: ButtonProps;
  transformSelectedKeys?: (key: string) => React.ReactNode;
  clearable?: boolean;
  showSelected?: boolean;
};

export const DropdownSelect = ({
  children,
  options,
  onSelectionChange,
  selectedKeys,
  selectionMode = 'single',
  triggerClassName,
  triggerProps,
  transformSelectedKeys,
  clearable = true,
  showSelected = true,
  ...props
}: Props) => {
  const onClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onSelectionChange?.([] as never);
  };

  return (
    <Dropdown placement='bottom-start' {...props}>
      <DropdownTrigger className='!opacity-100 !scale-100'>
        <Button
          disableAnimation
          disableRipple
          // startContent={<BiPlusCircle className='size-5' />}
          endContent={
            selectedKeys?.length && clearable ? (
              <TiDeleteOutline onClick={onClear} className='size-5 text-default-600 rounded-full' />
            ) : (
              <BiChevronDown className='size-5' />
            )
          }
          variant='bordered'
          className={cn(
            'flex border-2 hover:border-default-400 border-default-200 pr-1 justify-between',
            triggerClassName,
          )}
          {...triggerProps}
        >
          <div className='flex items-center gap-1'>
            {children}
            {showSelected && (
              <>
                <span className='h-full w-[1px] bg-divider' />
                {selectedKeys?.map((key) => (
                  <Chip
                    classNames={{ content: 'pl-1.5 text-[8px]', base: 'h-5 px-0.5' }}
                    key={key}
                    radius='sm'
                    size='sm'
                    variant='faded'
                  >
                    {transformSelectedKeys?.(key) ?? key}
                  </Chip>
                ))}
              </>
            )}
          </div>
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        disallowEmptySelection
        aria-label='Table Columns'
        closeOnSelect={false}
        selectedKeys={selectedKeys}
        selectionMode={selectionMode}
        onSelectionChange={onSelectionChange}
      >
        {options.map((option) => (
          <DropdownItem
            key={option.key}
            description={option.description}
            shortcut={option.shortcut}
            startContent={option.startContent}
            endContent={option.endContent}
            color={option.color}
            showDivider={option.showDivider}
            className={cn(option.className, 'capitalize')}
          >
            {option.title}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

export interface FormDropdownSelectProps<T extends FieldValues> extends Props {
  name: Path<T>;
  control: Control<T, unknown>;
  onValueChange?: (value: PathValue<T, Path<T>>) => void;
  showSelected?: boolean;
  showError?: boolean;
  shouldUnregister?: boolean;
}

export const FormDropDownSelect = <T extends FieldValues>({
  control,
  onValueChange,
  name,
  selectionMode = 'single',
  showSelected = false,
  showError = true,
  shouldUnregister = undefined,
  ...rest
}: FormDropdownSelectProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, onBlur, value } }) => {
        const onHandleChange = (keys: SharedSelection) => {
          const val = keys ? Array.from(keys)[0] : null;
          if (onValueChange) {
            onChange(val as PathValue<T, Path<T>>);
          }
        };
        return (
          <DropdownSelect
            {...rest}
            selectedKeys={value != null ? [`${value}`] : undefined}
            selectionMode={selectionMode}
            onBlur={onBlur}
            onSelectionChange={onHandleChange}
          />
        );
      }}
    />
  );
};
